/**
 * HC-4: "The card is tokenized in the customer's browser by the provider's own
 * script and sent directly to the provider. The card number never reaches our
 * servers."
 *
 * This module mirrors the backend's providers/ folder: it is the only file in the
 * frontend that knows a specific provider's tokenization API. Everything above it
 * asks the registry for a tokenizer by the `library` name the adapter declared,
 * so adding a provider means adding a file here and a line in the registry —
 * never touching the checkout page itself.
 */
let scriptPromise = null;

function loadScript(src) {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing && window.Accept) { resolve(); return; }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Could not load the secure payment library. Check your connection and try again.'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * @returns {Promise<{dataDescriptor: string, dataValue: string}>}
 * @throws  {Error} with a customer-readable message (HC-9)
 */
export async function tokenize({ config, card, billing = {} }) {
  await loadScript(config.scriptUrl);

  if (!window.Accept) {
    throw new Error('The secure payment library did not load. Please refresh and try again.');
  }

  const expiry = card.expiry;

  return new Promise((resolve, reject) => {
    window.Accept.dispatchData(
      {
        authData: { clientKey: config.clientKey, apiLoginID: config.apiLoginId },
        cardData: {
          cardNumber: card.number,
          month: String(expiry.month).padStart(2, '0'),
          year: String(expiry.year),
          cardCode: card.cvc,
          ...(billing.postalCode ? { zip: billing.postalCode } : {}),
          ...(billing.name ? { fullName: billing.name } : {}),
        },
      },
      (response) => {
        if (response?.messages?.resultCode === 'Ok' && response.opaqueData?.dataValue) {
          resolve({
            dataDescriptor: response.opaqueData.dataDescriptor,
            dataValue: response.opaqueData.dataValue,
          });
          return;
        }

        // HC-9: the provider's validation codes are translated into something a
        // customer can act on. Their raw text is often "E_WC_05" or similar.
        const first = response?.messages?.message?.[0];
        reject(new Error(translate(first?.code, first?.text)));
      },
    );
  });
}

/**
 * Things the customer can fix themselves.
 */
const CUSTOMER_FIXABLE = {
  E_WC_05: 'Please check the card number and try again.',
  E_WC_06: 'Please check the expiry month.',
  E_WC_07: 'Please check the expiry year.',
  E_WC_08: 'That card has expired.',
  E_WC_15: 'Please check the security code on your card.',
  E_WC_16: 'Please check the postcode for this card.',
  E_WC_17: 'Please check the name on the card.',
  E_WC_18: 'Please check the country for this card.',
  E_WC_23: 'Please check the postcode for this card.',
};

/**
 * Things only the *merchant* can fix — a wrong public client key, a mismatched
 * API login id, an account not enabled for this. Retrying cannot help, so the
 * customer is told to contact the merchant rather than to try again.
 */
const MERCHANT_MISCONFIGURED = new Set(['E_WC_10', 'E_WC_13', 'E_WC_14', 'E_WC_19', 'E_WC_01']);

const CONTACT_MERCHANT =
  'This payment could not be processed right now. Please contact the merchant — you have not been charged.';

function translate(code, fallbackText) {
  if (CUSTOMER_FIXABLE[code]) return CUSTOMER_FIXABLE[code];

  if (MERCHANT_MISCONFIGURED.has(code)) {
    /**
     * The merchant needs the real reason; the customer must not see it. The
     * provider's own text names its library ("Accept.js is not loaded
     * correctly"), which would put a processor's name in front of a customer —
     * exactly what the hosted checkout exists to prevent.
     */
      console.error(
      `[checkout] tokenization rejected: ${code} — ${fallbackText}\n` +
        'This is a merchant configuration problem. Check the public client key and ' +
        'API login id under Payment providers.',
    );
    return CONTACT_MERCHANT;
  }

  /**
   * Everything unmapped falls back to a generic line. Passing the provider's own
   * text through was a mistake: it leaks library and vendor names to customers,
   * and the text is written for developers, not for someone holding a card.
   */
  console.error(`[checkout] tokenization rejected: ${code} — ${fallbackText}`);
  return 'We could not process those card details. Please check them and try again.';
}

export default { tokenize };
